// src/features/staff/pages/StaffDetailPage.tsx
// COMPLETE REDESIGNED STAFF DETAIL PAGE

import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import * as staffApi from '../services/staffApi';
import type { StaffMember, StaffPermission, StaffActivity, StaffShift, StaffSalesSummary } from '../services/staffApi';
import {
    ArrowLeft, Mail, Phone, Shield, Key, Clock, Building2, Store,
    Pencil, X, CheckCircle2, XCircle, TrendingUp, DollarSign, Activity,
    ShoppingCart, History, Ban, KeyRound, Calendar, User,
    ChevronDown, ChevronRight, Tag, Search, Filter, Download,
    RefreshCw, AlertCircle, Info, Eye, FileText, LogIn, LogOut,
    Settings, UserPlus, UserX, Edit, Lock, Unlock, CreditCard,
    BarChart3, ArrowUpRight, ArrowDownRight, Users, Hash, AtSign,
    MapPin, Briefcase
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0.00';
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

const formatDateTime = (date: string) => {
    try { return new Date(date).toLocaleString('en-NG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return 'N/A'; }
};

const formatTimeAgo = (date: string) => {
    try {
        const now = new Date();
        const then = new Date(date);
        const diffMs = now.getTime() - then.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(date);
    } catch {
        return 'N/A';
    }
};

// ============================================================
// ACTIVITY ICON MAPPER
// ============================================================

const getActivityIcon = (action: string) => {
    const actionLower = action.toLowerCase();
    if (actionLower.includes('create') || actionLower.includes('created')) return { icon: UserPlus, color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (actionLower.includes('update') || actionLower.includes('edit')) return { icon: Edit, color: 'text-blue-600', bg: 'bg-blue-100' };
    if (actionLower.includes('delete') || actionLower.includes('deactivate')) return { icon: UserX, color: 'text-rose-600', bg: 'bg-rose-100' };
    if (actionLower.includes('activate') || actionLower.includes('reactivate')) return { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (actionLower.includes('password') || actionLower.includes('reset')) return { icon: Lock, color: 'text-amber-600', bg: 'bg-amber-100' };
    if (actionLower.includes('pin')) return { icon: KeyRound, color: 'text-purple-600', bg: 'bg-purple-100' };
    if (actionLower.includes('login')) return { icon: LogIn, color: 'text-indigo-600', bg: 'bg-indigo-100' };
    if (actionLower.includes('permission') || actionLower.includes('role')) return { icon: Shield, color: 'text-cyan-600', bg: 'bg-cyan-100' };
    return { icon: Activity, color: 'text-slate-600', bg: 'bg-slate-100' };
};

const getActionBadge = (action: string) => {
    const actionLower = action.toLowerCase();
    if (actionLower.includes('create')) return 'success';
    if (actionLower.includes('update') || actionLower.includes('edit')) return 'info';
    if (actionLower.includes('delete') || actionLower.includes('deactivate')) return 'danger';
    if (actionLower.includes('activate')) return 'success';
    if (actionLower.includes('password') || actionLower.includes('reset')) return 'warning';
    if (actionLower.includes('pin')) return 'neutral';
    return 'neutral';
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function StaffDetailPage() {
    const { staffId } = useParams<{ staffId: string }>();
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [activeSection, setActiveSection] = useState<string>('profile');

    // Activity filters
    const [activitySearch, setActivitySearch] = useState('');
    const [activityFilter, setActivityFilter] = useState<string>('all');
    const [activityPage, setActivityPage] = useState(1);
    const activityLimit = 20;

    // Modals
    const [showEditForm, setShowEditForm] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showSetPin, setShowSetPin] = useState(false);
    const [confirmAction, setConfirmAction] = useState<string | null>(null);
    const [selectedActivity, setSelectedActivity] = useState<StaffActivity | null>(null);

    const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', phone: '', employeeId: '' });
    const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
    const [pinForm, setPinForm] = useState({ pin: '' });

    // ==================== QUERIES ====================

    const { data: staff, isLoading, isError, refetch } = useQuery<StaffMember>({
        queryKey: ['staff', staffId],
        queryFn: () => staffApi.getStaffById(tenantId!, staffId!),
        enabled: !!tenantId && !!staffId,
    });

    const { data: permissions } = useQuery<StaffPermission>({
        queryKey: ['staff-permissions', staffId],
        queryFn: () => staffApi.getStaffPermissions(tenantId!, staffId!),
        enabled: !!staffId,
    });

    const { data: activities } = useQuery<StaffActivity[]>({
        queryKey: ['staff-activity', staffId],
        queryFn: () => staffApi.getStaffActivity(tenantId!, staffId!, 100),
        enabled: !!staffId && activeSection === 'activity',
    });

    const { data: shifts } = useQuery<StaffShift[]>({
        queryKey: ['staff-shifts', staffId],
        queryFn: () => staffApi.getStaffShifts(tenantId!, staffId!, 50),
        enabled: !!staffId && activeSection === 'shifts',
    });

    const { data: salesData } = useQuery<StaffSalesSummary>({
        queryKey: ['staff-sales', staffId],
        queryFn: () => staffApi.getStaffSales(tenantId!, staffId!),
        enabled: !!staffId && activeSection === 'sales',
    });

    // ==================== MUTATIONS ====================

    const updateMutation = useMutation({
        mutationFn: (data: Record<string, unknown>) => staffApi.updateStaff(tenantId!, staffId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['staff', staffId] });
            addToast({ type: 'success', title: 'Profile Updated', message: 'Staff information has been updated successfully.' });
            setShowEditForm(false);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Update Failed', message: err?.response?.data?.error?.message || 'Failed to update profile' }),
    });

    const resetPasswordMutation = useMutation({
        mutationFn: () => staffApi.resetStaffPassword(tenantId!, staffId!, passwordForm.newPassword),
        onSuccess: () => {
            addToast({ type: 'success', title: 'Password Reset', message: 'Staff member will need to use the new password on next login.' });
            setShowResetPassword(false);
            setPasswordForm({ newPassword: '', confirmPassword: '' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Reset Failed', message: err?.response?.data?.error?.message || 'Failed to reset password' }),
    });

    const setPinMutation = useMutation({
        mutationFn: () => staffApi.setStaffPin(tenantId!, staffId!, pinForm.pin),
        onSuccess: () => {
            addToast({ type: 'success', title: 'PIN Set', message: 'POS PIN has been set successfully.' });
            setShowSetPin(false);
            setPinForm({ pin: '' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'PIN Failed', message: err?.response?.data?.error?.message || 'Failed to set PIN' }),
    });

    const toggleMutation = useMutation({
        mutationFn: (activate: boolean) => activate ? staffApi.reactivateStaff(tenantId!, staffId!) : staffApi.deactivateStaff(tenantId!, staffId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['staff', staffId] });
            addToast({ type: 'success', title: staff?.isActive ? 'Staff Deactivated' : 'Staff Activated', message: `${staff?.firstName} ${staff?.lastName} has been ${staff?.isActive ? 'deactivated' : 'activated'}.` });
            setConfirmAction(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Action Failed', message: err?.message || 'Failed to update status' }),
    });

    // ==================== DERIVED DATA ====================

    const filteredActivities = useMemo(() => {
        if (!activities) return [];
        let filtered = [...activities];

        // Filter by action type
        if (activityFilter !== 'all') {
            filtered = filtered.filter(a => a.action.toLowerCase().includes(activityFilter.toLowerCase()));
        }

        // Search
        if (activitySearch) {
            const term = activitySearch.toLowerCase();
            filtered = filtered.filter(a =>
                a.action.toLowerCase().includes(term) ||
                a.entityType.toLowerCase().includes(term) ||
                (a.entityId && a.entityId.includes(term))
            );
        }

        return filtered;
    }, [activities, activityFilter, activitySearch]);

    const paginatedActivities = useMemo(() => {
        const start = (activityPage - 1) * activityLimit;
        return filteredActivities.slice(start, start + activityLimit);
    }, [filteredActivities, activityPage]);

    const totalActivityPages = Math.ceil(filteredActivities.length / activityLimit);

    const activityTypes = useMemo(() => {
        if (!activities) return [];
        const types = new Set(activities.map(a => a.action.split('_')[0].toLowerCase()));
        return Array.from(types);
    }, [activities]);

    // ==================== LOADING / ERROR ====================

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <Spinner size="xl" variant="emerald" />
        </div>
    );

    if (isError || !staff) return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <ErrorState
                title="Staff not found"
                message="The staff member you're looking for doesn't exist or has been removed."
                onRetry={() => refetch()}
                onBack={() => navigate('/staff')}
            />
        </div>
    );

    const sections = [
        { key: 'profile', label: 'Overview', icon: User, description: 'Personal & system info' },
        { key: 'roles', label: 'Permissions', icon: Shield, description: 'Roles & access rights' },
        { key: 'sales', label: 'Sales', icon: BarChart3, description: 'Performance metrics' },
        { key: 'shifts', label: 'Shifts', icon: Clock, description: 'Shift history' },
        { key: 'activity', label: 'Audit Log', icon: FileText, description: 'Activity trail' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="max-w-[1400px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Back Button */}
                <motion.button
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/staff')}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-600 group transition-colors">
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back to Staff Management
                </motion.button>

                {/* ========== PROFILE HEADER CARD ========== */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-white rounded-3xl border border-slate-200 shadow-xl">

                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-white to-teal-50/30" />
                    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-emerald-100/20 to-transparent rounded-full blur-3xl" />

                    <div className="relative p-6 lg:p-8">
                        <div className="flex flex-col lg:flex-row items-start gap-6">
                            {/* Avatar */}
                            <div className="relative">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-3xl font-black shadow-xl ring-4 ring-white">
                                    {staff.firstName?.[0]}{staff.lastName?.[0]}
                                </div>
                                <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center ${staff.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                                    {staff.isActive ? <CheckCircle2 size={12} className="text-white" /> : <XCircle size={12} className="text-white" />}
                                </div>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap mb-2">
                                    <h1 className="text-2xl lg:text-3xl font-black text-slate-900">
                                        {staff.firstName} {staff.lastName}
                                    </h1>
                                    <Badge variant={staff.isActive ? 'success' : 'danger'} size="md">
                                        {staff.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                    {staff.posPin && (
                                        <Badge variant="info" size="md">
                                            <KeyRound size={12} className="mr-1" /> PIN Set
                                        </Badge>
                                    )}
                                    {staff.posAccess && staff.posAccess !== 'none' && (
                                        <Badge variant="info" size="md">
                                            {staff.posAccess === 'retail' ? '🏪 Retail POS' :
                                                staff.posAccess === 'bom' ? '🍽️ Menu POS' :
                                                    '🏪🍽️ Both POS'}
                                        </Badge>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mt-3">
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <AtSign size={14} className="text-slate-400 flex-shrink-0" />
                                        <span className="truncate">{staff.email}</span>
                                    </div>
                                    {staff.phone && (
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <Phone size={14} className="text-slate-400 flex-shrink-0" />
                                            <span>{staff.phone}</span>
                                        </div>
                                    )}
                                    {staff.employeeId && (
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <Hash size={14} className="text-slate-400 flex-shrink-0" />
                                            <span>{staff.employeeId}</span>
                                        </div>
                                    )}
                                    {staff.businessName && (
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <Briefcase size={14} className="text-slate-400 flex-shrink-0" />
                                            <span>{staff.businessName}</span>
                                        </div>
                                    )}
                                    {staff.branchName && (
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <MapPin size={14} className="text-slate-400 flex-shrink-0" />
                                            <span>{staff.branchName}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <Calendar size={14} className="text-slate-400 flex-shrink-0" />
                                        <span>Joined {formatDate(staff.createdAt)}</span>
                                    </div>
                                </div>

                                {/* Roles */}
                                <div className="flex flex-wrap items-center gap-2 mt-4">
                                    {staff.roles?.length > 0 ? (
                                        staff.roles.map(r => (
                                            <Badge key={r.id} variant="neutral" size="sm" className="font-medium">
                                                {r.name}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-slate-400 italic">No roles assigned</span>
                                    )}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    leftIcon={<Pencil size={14} />}
                                    onClick={() => {
                                        setEditForm({
                                            firstName: staff.firstName,
                                            lastName: staff.lastName,
                                            email: staff.email,
                                            phone: staff.phone || '',
                                            employeeId: staff.employeeId || ''
                                        });
                                        setShowEditForm(true);
                                    }}>
                                    Edit
                                </Button>
                                <Button
                                    size="sm"
                                    variant={staff.isActive ? 'danger' : 'success'}
                                    leftIcon={staff.isActive ? <Ban size={14} /> : <CheckCircle2 size={14} />}
                                    onClick={() => setConfirmAction(staff.isActive ? 'deactivate' : 'activate')}>
                                    {staff.isActive ? 'Deactivate' : 'Activate'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== QUICK STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        {
                            label: 'Last Login',
                            value: staff.lastLoginAt ? formatTimeAgo(staff.lastLoginAt) : 'Never',
                            sub: staff.lastLoginAt ? formatDateTime(staff.lastLoginAt) : undefined,
                            icon: LogIn,
                            gradient: 'from-blue-500 to-indigo-600'
                        },
                        {
                            label: 'Roles Assigned',
                            value: staff.roles?.length || 0,
                            sub: 'active roles',
                            icon: Shield,
                            gradient: 'from-purple-500 to-violet-600'
                        },
                        {
                            label: 'PIN Status',
                            value: staff.posPin ? 'Configured' : 'Not Set',
                            sub: staff.posPin ? 'POS access enabled' : 'No POS access',
                            icon: KeyRound,
                            gradient: staff.posPin ? 'from-emerald-500 to-teal-600' : 'from-slate-400 to-slate-500'
                        },
                        {
                            label: 'Member Since',
                            value: formatDate(staff.createdAt),
                            sub: `${Math.floor((Date.now() - new Date(staff.createdAt).getTime()) / 86400000)} days ago`,
                            icon: Calendar,
                            gradient: 'from-amber-500 to-orange-600'
                        },
                    ].map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all">
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
                                <stat.icon className="w-4 h-4 text-white" />
                            </div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                            <p className="text-lg font-bold text-slate-900 mt-0.5">{stat.value}</p>
                            {stat.sub && <p className="text-[10px] text-slate-400 mt-0.5">{stat.sub}</p>}
                        </motion.div>
                    ))}
                </div>

                {/* ========== SECTION NAVIGATION ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex gap-1 overflow-x-auto">
                        {sections.map(s => (
                            <button
                                key={s.key}
                                onClick={() => setActiveSection(s.key)}
                                className={clsx(
                                    'flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex-shrink-0',
                                    activeSection === s.key
                                        ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                )}>
                                <s.icon size={16} />
                                <div className="text-left">
                                    <div>{s.label}</div>
                                    <div className="text-[10px] opacity-60 hidden sm:block">{s.description}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* ========== SECTION CONTENT ========== */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeSection}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.15 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 lg:p-8 min-h-[400px]">

                        {/* ==================== OVERVIEW ==================== */}
                        {activeSection === 'profile' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Personal Information */}
                                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                                        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                                            <User size={16} className="text-emerald-500" />
                                            Personal Information
                                        </h3>
                                        <div className="space-y-1">
                                            {[
                                                { label: 'Full Name', value: `${staff.firstName} ${staff.lastName}`, icon: User },
                                                { label: 'Email Address', value: staff.email, icon: AtSign },
                                                { label: 'Phone Number', value: staff.phone || 'Not provided', icon: Phone },
                                                { label: 'Employee ID', value: staff.employeeId || 'Not assigned', icon: Hash },
                                            ].map((field, i) => (
                                                <div key={i} className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-white transition-colors">
                                                    <span className="text-sm text-slate-500 flex items-center gap-2">
                                                        <field.icon size={14} className="text-slate-400" />
                                                        {field.label}
                                                    </span>
                                                    <span className="text-sm font-medium text-slate-900">{field.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* System Information */}
                                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                                        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                                            <Settings size={16} className="text-emerald-500" />
                                            System Information
                                        </h3>
                                        <div className="space-y-1">
                                            {[
                                                { label: 'Account Status', value: staff.isActive ? 'Active' : 'Inactive', icon: Activity, badge: staff.isActive ? 'success' as const : 'danger' as const },
                                                { label: 'POS PIN', value: staff.posPin ? 'Configured' : 'Not Set', icon: KeyRound, badge: staff.posPin ? 'success' as const : 'warning' as const },
                                                { label: 'Business', value: staff.businessName || 'Not assigned', icon: Briefcase },
                                                { label: 'Branch', value: staff.branchName || 'Not assigned', icon: MapPin },
                                                { label: 'Last Login', value: staff.lastLoginAt ? formatDateTime(staff.lastLoginAt) : 'Never', icon: LogIn },
                                                { label: 'Member Since', value: formatDate(staff.createdAt), icon: Calendar },
                                            ].map((field, i) => (
                                                <div key={i} className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-white transition-colors">
                                                    <span className="text-sm text-slate-500 flex items-center gap-2">
                                                        <field.icon size={14} className="text-slate-400" />
                                                        {field.label}
                                                    </span>
                                                    <span className="text-sm font-medium text-slate-900 flex items-center gap-2">
                                                        {field.value}
                                                        {field.badge && <Badge variant={field.badge} size="sm">{field.value}</Badge>}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="flex flex-wrap gap-3">
                                    <Button size="sm" variant="secondary" leftIcon={<Lock size={14} />} onClick={() => setShowResetPassword(true)}>
                                        Reset Password
                                    </Button>
                                    <Button size="sm" variant="secondary" leftIcon={<KeyRound size={14} />} onClick={() => setShowSetPin(true)}>
                                        Set POS PIN
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* ==================== PERMISSIONS ==================== */}
                        {/* ==================== PERMISSIONS ==================== */}
                        {activeSection === 'roles' && (
                            <div className="space-y-6">
                                {/* Summary Banner */}
                                {permissions && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-5">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                                                    <Shield size={20} className="text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Roles</p>
                                                    <p className="text-2xl font-black text-blue-900">{permissions.roles?.length || 0}</p>
                                                </div>
                                            </div>
                                        </motion.div>
                                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                                            className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 p-5">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                                                    <CheckCircle2 size={20} className="text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Permissions</p>
                                                    <p className="text-2xl font-black text-emerald-900">{permissions.effectivePermissions?.length || 0}</p>
                                                </div>
                                            </div>
                                        </motion.div>
                                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                                            className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl border border-purple-200 p-5">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 shadow-md">
                                                    <Key size={20} className="text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Access Level</p>
                                                    <p className="text-lg font-black text-purple-900">
                                                        {permissions.roles?.some(r => r.name?.toLowerCase().includes('admin')) ? 'Admin' :
                                                            permissions.roles?.length > 0 ? 'Staff' : 'None'}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </div>
                                )}

                                {/* Roles Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                            <div className="p-1.5 rounded-lg bg-blue-100">
                                                <Shield size={16} className="text-blue-600" />
                                            </div>
                                            Assigned Roles
                                        </h3>
                                        {permissions?.roles?.length ? (
                                            <Badge variant="info" size="sm">{permissions.roles.length} role{permissions.roles.length !== 1 ? 's' : ''}</Badge>
                                        ) : null}
                                    </div>

                                    {permissions?.roles?.length ? (
                                        <div className="space-y-3">
                                            {permissions.roles.map((role, idx) => (
                                                <motion.div
                                                    key={role.id}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.1 }}
                                                    className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-all">
                                                    {/* Role Header */}
                                                    <div className="px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md">
                                                                    {role.name.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-bold text-blue-900 text-base">{role.name}</h4>
                                                                    <p className="text-xs text-blue-500">{role.permissions?.length || 0} permission{(role.permissions?.length || 0) !== 1 ? 's' : ''}</p>
                                                                </div>
                                                            </div>
                                                            <Badge variant="info" size="sm" className="!bg-blue-100 !text-blue-700 !border-blue-200">
                                                                Active Role
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    {/* Permissions Grid */}
                                                    <div className="p-4">
                                                        {role.permissions?.length ? (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                                {role.permissions.map(perm => (
                                                                    <div key={perm.id}
                                                                        className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
                                                                        <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 group-hover:scale-125 transition-transform" />
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="text-xs font-semibold text-slate-700 truncate">{perm.code}</p>
                                                                            <p className="text-[10px] text-slate-400">
                                                                                {perm.resource}:{perm.action}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-slate-400 text-center py-4">No permissions in this role</p>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                                                <Shield size={28} className="text-slate-300" />
                                            </div>
                                            <h4 className="text-sm font-bold text-slate-500">No Roles Assigned</h4>
                                            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
                                                This staff member has no roles yet. Assign roles to grant system access.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Effective Permissions Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                            <div className="p-1.5 rounded-lg bg-emerald-100">
                                                <CheckCircle2 size={16} className="text-emerald-600" />
                                            </div>
                                            Effective Permissions
                                        </h3>
                                        {permissions?.effectivePermissions?.length ? (
                                            <Badge variant="success" size="sm">{permissions.effectivePermissions.length} total</Badge>
                                        ) : null}
                                    </div>

                                    {permissions?.effectivePermissions?.length ? (
                                        <div>
                                            {(() => {
                                                const grouped = permissions.effectivePermissions.reduce((acc: Record<string, typeof permissions.effectivePermissions>, perm) => {
                                                    const resource = perm.resource || 'other';
                                                    if (!acc[resource]) acc[resource] = [];
                                                    acc[resource].push(perm);
                                                    return acc;
                                                }, {} as Record<string, typeof permissions.effectivePermissions>);

                                                const resources = Object.keys(grouped);

                                                return (
                                                    <>
                                                        {/* Resource Tabs */}
                                                        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
                                                            <button className="px-3 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all border bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm">
                                                                All ({permissions.effectivePermissions.length})
                                                            </button>
                                                            {resources.map(resource => (
                                                                <button key={resource}
                                                                    className="px-3 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all border bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700">
                                                                    {resource.replace(/_/g, ' ')} ({grouped[resource].length})
                                                                </button>
                                                            ))}
                                                        </div>

                                                        {/* Compact Permissions Table */}
                                                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                                            <div className="max-h-[400px] overflow-y-auto">
                                                                <table className="min-w-full divide-y divide-slate-100">
                                                                    <thead className="sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10">
                                                                        <tr>
                                                                            <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Resource</th>
                                                                            <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Action</th>
                                                                            <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Permission Code</th>
                                                                            <th className="px-4 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-16">Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-slate-50">
                                                                        {permissions.effectivePermissions.map((perm, idx) => (
                                                                            <motion.tr
                                                                                key={perm.code}
                                                                                initial={{ opacity: 0 }}
                                                                                animate={{ opacity: 1 }}
                                                                                transition={{ delay: Math.min(idx * 0.01, 0.3) }}
                                                                                className="hover:bg-emerald-50/30 transition-colors group">
                                                                                <td className="px-4 py-2.5">
                                                                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-[10px] font-semibold text-slate-600 capitalize">
                                                                                        {perm.resource.replace(/_/g, ' ')}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-4 py-2.5">
                                                                                    <span className="text-xs font-medium text-slate-700 capitalize">
                                                                                        {perm.action.replace(/_/g, ' ')}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-4 py-2.5 hidden md:table-cell">
                                                                                    <code className="text-[10px] text-slate-400 font-mono bg-slate-50 px-1.5 py-0.5 rounded">
                                                                                        {perm.code}
                                                                                    </code>
                                                                                </td>
                                                                                <td className="px-4 py-2.5 text-center">
                                                                                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                                                                                        <CheckCircle2 size={12} className="text-emerald-600" />
                                                                                    </div>
                                                                                </td>
                                                                            </motion.tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>

                                                        {/* Quick Legend */}
                                                        <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-400">
                                                            <span className="flex items-center gap-1">
                                                                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Granted
                                                            </span>
                                                            <span>{permissions.effectivePermissions.length} permissions across {resources.length} resource{resources.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                                                <CheckCircle2 size={24} className="text-slate-300" />
                                            </div>
                                            <h4 className="text-sm font-bold text-slate-500">No Effective Permissions</h4>
                                            <p className="text-xs text-slate-400 mt-1">Assign roles to grant system access.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ==================== SALES ==================== */}
                        {activeSection === 'sales' && (
                            <div>
                                {!salesData || salesData.totalTransactions === 0 ? (
                                    <EmptyState icon={<BarChart3 size={48} />} title="No sales data" description="This staff member hasn't made any sales yet." size="md" />
                                ) : (
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {[
                                                { label: 'Total Sales', value: formatNaira(salesData.totalSales), icon: DollarSign, gradient: 'from-emerald-500 to-teal-600', trend: 'up' },
                                                { label: 'Transactions', value: salesData.totalTransactions, icon: ShoppingCart, gradient: 'from-blue-500 to-indigo-600' },
                                                { label: 'Avg Basket', value: formatNaira(salesData.averageBasket), icon: TrendingUp, gradient: 'from-purple-500 to-violet-600' },
                                                { label: 'Discounts', value: formatNaira(salesData.totalDiscounts), icon: Tag, gradient: 'from-amber-500 to-orange-600' },
                                            ].map((s, i) => (
                                                <motion.div
                                                    key={s.label}
                                                    initial={{ opacity: 0, scale: 0.95 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ delay: i * 0.05 }}
                                                    className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                                                    <div className={`p-2 rounded-lg bg-gradient-to-br ${s.gradient} w-fit mb-3 shadow-md`}>
                                                        <s.icon size={16} className="text-white" />
                                                    </div>
                                                    <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">{s.label}</p>
                                                    <p className="text-xl font-bold text-slate-900 mt-1">{s.value}</p>
                                                    {s.trend && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <ArrowUpRight size={12} className="text-emerald-500" />
                                                            <span className="text-[10px] text-emerald-600 font-medium">Active</span>
                                                        </div>
                                                    )}
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* Top Products */}
                                        {salesData.topProducts?.length > 0 && (
                                            <div>
                                                <h3 className="text-sm font-bold text-slate-700 mb-3">Top Products</h3>
                                                <div className="space-y-2">
                                                    {salesData.topProducts.slice(0, 5).map((product, i) => (
                                                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs font-bold text-slate-400 w-6">#{i + 1}</span>
                                                                <span className="text-sm font-medium text-slate-700">{product.productName}</span>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <span className="text-xs text-slate-500">Qty: {product.quantity}</span>
                                                                <span className="text-sm font-bold text-emerald-600">{formatNaira(product.revenue)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ==================== SHIFTS ==================== */}
                        {activeSection === 'shifts' && (
                            <div>
                                {!shifts || shifts.length === 0 ? (
                                    <EmptyState icon={<Clock size={48} />} title="No shifts" description="No shift history recorded." size="md" />
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-sm font-bold text-slate-700">Shift History ({shifts.length})</h3>
                                        </div>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                            {shifts.map(shift => (
                                                <div key={shift.id} className="bg-slate-50 rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition-colors">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant={shift.status === 'closed' ? 'success' : shift.status === 'open' ? 'warning' : 'neutral'} size="sm">
                                                                {shift.status}
                                                            </Badge>
                                                            {shift.branchName && (
                                                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                                                    <Store size={10} />{shift.branchName}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-slate-400">{formatDate(shift.openedAt)}</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                                        <div>
                                                            <span className="text-slate-400">Opening Cash:</span>
                                                            <span className="font-bold ml-1 text-slate-700">{formatNaira(shift.openingCash)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-400">Total Sales:</span>
                                                            <span className="font-bold ml-1 text-emerald-600">{formatNaira(shift.totalSales || '0')}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-400">Transactions:</span>
                                                            <span className="font-bold ml-1 text-slate-700">{shift.transactionCount}</span>
                                                        </div>
                                                        {shift.closedAt && (
                                                            <>
                                                                <div>
                                                                    <span className="text-slate-400">Counted:</span>
                                                                    <span className="font-bold ml-1 text-slate-700">{formatNaira(shift.countedCash || '0')}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <span className="text-slate-400">Variance:</span>
                                                                    <span className={clsx('font-bold ml-1',
                                                                        parseFloat(shift.cashVariance || '0') >= 0 ? 'text-emerald-600' : 'text-rose-600'
                                                                    )}>
                                                                        {formatNaira(shift.cashVariance || '0')}
                                                                    </span>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ==================== AUDIT LOG (ACTIVITY) ==================== */}
                        {activeSection === 'activity' && (
                            <div>
                                {!activities || activities.length === 0 ? (
                                    <EmptyState icon={<FileText size={48} />} title="No activity" description="No activity recorded for this staff member." size="md" />
                                ) : (
                                    <div className="space-y-4">
                                        {/* Activity Toolbar */}
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                                <FileText size={16} className="text-emerald-500" />
                                                Audit Log ({filteredActivities.length} entries)
                                            </h3>
                                            <div className="flex items-center gap-2">
                                                {/* Search */}
                                                <div className="relative">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search activities..."
                                                        value={activitySearch}
                                                        onChange={e => { setActivitySearch(e.target.value); setActivityPage(1); }}
                                                        className="pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs w-36 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                                    />
                                                    {activitySearch && (
                                                        <button onClick={() => setActivitySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                                                            <X size={12} className="text-slate-400 hover:text-slate-600" />
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Filter */}
                                                <select
                                                    value={activityFilter}
                                                    onChange={e => { setActivityFilter(e.target.value); setActivityPage(1); }}
                                                    className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-600">
                                                    <option value="all">All Actions</option>
                                                    {activityTypes.map(type => (
                                                        <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Activity Table */}
                                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-slate-100">
                                                    <thead>
                                                        <tr className="bg-slate-50">
                                                            <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider w-12">#</th>
                                                            <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Action</th>
                                                            <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Entity</th>
                                                            <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Details</th>
                                                            <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Timestamp</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {paginatedActivities.map((log, idx) => {
                                                            const { icon: ActivityIcon, color, bg } = getActivityIcon(log.action);
                                                            const globalIdx = ((activityPage - 1) * activityLimit) + idx + 1;

                                                            return (
                                                                <motion.tr
                                                                    key={log.id || idx}
                                                                    initial={{ opacity: 0 }}
                                                                    animate={{ opacity: 1 }}
                                                                    transition={{ delay: idx * 0.02 }}
                                                                    className="hover:bg-slate-50 cursor-pointer group"
                                                                    onClick={() => setSelectedActivity(log)}>
                                                                    <td className="px-4 py-3">
                                                                        <span className="text-xs text-slate-400 font-mono">{globalIdx}</span>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className={`p-1.5 rounded-lg ${bg} flex-shrink-0`}>
                                                                                <ActivityIcon size={14} className={color} />
                                                                            </div>
                                                                            <div>
                                                                                <Badge variant={getActionBadge(log.action)} size="sm" className="capitalize">
                                                                                    {log.action.replace(/_/g, ' ')}
                                                                                </Badge>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 hidden md:table-cell">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-xs font-medium text-slate-700 capitalize">{log.entityType}</span>
                                                                            {log.entityId && (
                                                                                <span className="text-[10px] text-slate-400 font-mono truncate max-w-[120px]" title={log.entityId}>
                                                                                    {log.entityId.slice(0, 8)}...
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 hidden lg:table-cell">
                                                                        <div className="text-xs text-slate-500 max-w-[300px] truncate">
                                                                            {log.newValues ? (
                                                                                <span>
                                                                                    {Object.keys(log.newValues).slice(0, 3).join(', ')}
                                                                                    {Object.keys(log.newValues).length > 3 ? '...' : ''}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-slate-400 italic">No details</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right">
                                                                        <div className="flex flex-col items-end">
                                                                            <span className="text-xs text-slate-500">{formatDateTime(log.createdAt)}</span>
                                                                            <span className="text-[10px] text-slate-400">{formatTimeAgo(log.createdAt)}</span>
                                                                        </div>
                                                                    </td>
                                                                </motion.tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Empty state for filtered results */}
                                            {paginatedActivities.length === 0 && (
                                                <div className="py-12 text-center">
                                                    <Search size={32} className="text-slate-300 mx-auto mb-2" />
                                                    <p className="text-sm text-slate-500">No activities match your filters</p>
                                                    <button
                                                        onClick={() => { setActivitySearch(''); setActivityFilter('all'); }}
                                                        className="text-xs text-emerald-600 hover:text-emerald-700 mt-1">
                                                        Clear filters
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Pagination */}
                                        {totalActivityPages > 1 && (
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs text-slate-500">
                                                    Showing {((activityPage - 1) * activityLimit) + 1}-{Math.min(activityPage * activityLimit, filteredActivities.length)} of {filteredActivities.length}
                                                </p>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => setActivityPage(p => Math.max(1, p - 1))}
                                                        disabled={activityPage === 1}
                                                        className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30">
                                                        <ChevronRight size={14} className="rotate-180" />
                                                    </button>
                                                    <span className="text-xs font-medium text-slate-600 px-2">{activityPage} / {totalActivityPages}</span>
                                                    <button
                                                        onClick={() => setActivityPage(p => Math.min(totalActivityPages, p + 1))}
                                                        disabled={activityPage === totalActivityPages}
                                                        className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30">
                                                        <ChevronRight size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>

                {/* ========== MODALS ========== */}

                {/* Edit Profile Modal */}
                <Modal open={showEditForm} onClose={() => setShowEditForm(false)} title="Edit Staff Profile" icon={<Pencil size={18} className="text-blue-600" />}>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="First Name" value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} />
                            <Input label="Last Name" value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: e.target.value })} />
                        </div>
                        <Input label="Email Address" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} disabled />
                        <Input label="Phone Number" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+234..." />
                        <Input label="Employee ID" value={editForm.employeeId} onChange={e => setEditForm({ ...editForm, employeeId: e.target.value })} />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button variant="secondary" onClick={() => setShowEditForm(false)}>Cancel</Button>
                        <Button onClick={() => updateMutation.mutate(editForm)} isLoading={updateMutation.isPending}>Save Changes</Button>
                    </div>
                </Modal>

                {/* Reset Password Modal */}
                <Modal open={showResetPassword} onClose={() => { setShowResetPassword(false); setPasswordForm({ newPassword: '', confirmPassword: '' }); }} title="Reset Password" icon={<Lock size={18} className="text-amber-600" />}>
                    <div className="space-y-3">
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700 flex items-start gap-2">
                            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                            <span>Staff member will be required to use this new password on their next login.</span>
                        </div>
                        <Input
                            label="New Password"
                            type="password"
                            value={passwordForm.newPassword}
                            onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            hint="Minimum 8 characters with uppercase, lowercase, number, and special character"
                        />
                        <Input
                            label="Confirm Password"
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                            error={passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword ? 'Passwords do not match' : undefined}
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button variant="secondary" onClick={() => { setShowResetPassword(false); setPasswordForm({ newPassword: '', confirmPassword: '' }); }}>Cancel</Button>
                        <Button
                            onClick={() => resetPasswordMutation.mutate()}
                            isLoading={resetPasswordMutation.isPending}
                            disabled={!passwordForm.newPassword || passwordForm.newPassword !== passwordForm.confirmPassword || passwordForm.newPassword.length < 8}>
                            Reset Password
                        </Button>
                    </div>
                </Modal>

                {/* Set PIN Modal */}
                <Modal open={showSetPin} onClose={() => { setShowSetPin(false); setPinForm({ pin: '' }); }} title="Set POS PIN" icon={<KeyRound size={18} className="text-purple-600" />}>
                    <div className="space-y-4">
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700 flex items-start gap-2">
                            <Info size={16} className="flex-shrink-0 mt-0.5" />
                            <span>This PIN will be used for POS terminal login. Must be exactly 6 digits.</span>
                        </div>
                        <Input
                            label="6-Digit PIN"
                            type="password"
                            maxLength={6}
                            value={pinForm.pin}
                            onChange={e => setPinForm({ pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                            placeholder="Enter 6-digit PIN"
                        />
                        <div className="flex justify-center gap-3">
                            {[0, 1, 2, 3, 4, 5].map(i => (
                                <div key={i} className={clsx(
                                    'w-12 h-12 rounded-xl border-2 flex items-center justify-center text-xl font-bold transition-all',
                                    pinForm.pin[i]
                                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700 shadow-sm'
                                        : 'border-slate-200 text-slate-300 bg-slate-50'
                                )}>
                                    {pinForm.pin[i] ? '•' : ''}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button variant="secondary" onClick={() => { setShowSetPin(false); setPinForm({ pin: '' }); }}>Cancel</Button>
                        <Button onClick={() => setPinMutation.mutate()} isLoading={setPinMutation.isPending} disabled={pinForm.pin.length !== 6}>
                            Set PIN
                        </Button>
                    </div>
                </Modal>

                {/* Activity Detail Modal */}
                <Modal
                    open={!!selectedActivity}
                    onClose={() => setSelectedActivity(null)}
                    title="Activity Details"
                    icon={<FileText size={18} className="text-emerald-600" />}>
                    {selectedActivity && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-xl ${getActivityIcon(selectedActivity.action).bg}`}>
                                    {(() => {
                                        const { icon: Icon, color } = getActivityIcon(selectedActivity.action);
                                        return <Icon size={20} className={color} />;
                                    })()}
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 capitalize">{selectedActivity.action.replace(/_/g, ' ')}</h3>
                                    <p className="text-sm text-slate-500">{selectedActivity.entityType}</p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-xs text-slate-500">Timestamp</span>
                                    <span className="text-xs font-medium text-slate-700">{formatDateTime(selectedActivity.createdAt)}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-xs text-slate-500">Action</span>
                                    <Badge variant={getActionBadge(selectedActivity.action)} size="sm" className="capitalize">
                                        {selectedActivity.action.replace(/_/g, ' ')}
                                    </Badge>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-xs text-slate-500">Entity Type</span>
                                    <span className="text-xs font-medium text-slate-700 capitalize">{selectedActivity.entityType}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-xs text-slate-500">Entity ID</span>
                                    <span className="text-xs font-medium text-slate-700 font-mono">
                                        {selectedActivity.entityId || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            {/* Old Values */}
                            {selectedActivity.oldValues && Object.keys(selectedActivity.oldValues).length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                        <ArrowDownRight size={12} /> Previous Values
                                    </h4>
                                    <div className="bg-rose-50 rounded-xl p-3 border border-rose-100">
                                        <pre className="text-xs text-slate-700 overflow-x-auto font-mono">
                                            {JSON.stringify(selectedActivity.oldValues, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}

                            {/* New Values */}
                            {selectedActivity.newValues && Object.keys(selectedActivity.newValues).length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                        <ArrowUpRight size={12} /> New Values
                                    </h4>
                                    <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                                        <pre className="text-xs text-slate-700 overflow-x-auto font-mono">
                                            {JSON.stringify(selectedActivity.newValues, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}

                            {/* If no values at all */}
                            {(!selectedActivity.oldValues || Object.keys(selectedActivity.oldValues).length === 0) &&
                                (!selectedActivity.newValues || Object.keys(selectedActivity.newValues).length === 0) && (
                                    <div className="text-center py-4 text-xs text-slate-400 italic">
                                        No value changes recorded for this activity.
                                    </div>
                                )}
                        </div>
                    )}
                </Modal>

                {/* ========== CONFIRMATION ========== */}
                <AnimatePresence>
                    {confirmAction && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                            onClick={() => setConfirmAction(null)}>
                            <motion.div
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${confirmAction === 'deactivate' ? 'bg-rose-100' : 'bg-emerald-100'}`}>
                                    {confirmAction === 'deactivate' ? <Ban size={28} className="text-rose-500" /> : <CheckCircle2 size={28} className="text-emerald-500" />}
                                </div>
                                <h3 className="text-lg font-bold text-slate-900">
                                    {confirmAction === 'deactivate' ? 'Deactivate Staff?' : 'Activate Staff?'}
                                </h3>
                                <p className="text-sm text-slate-500 mt-2">
                                    {staff.firstName} {staff.lastName} will be {confirmAction === 'deactivate' ? 'deactivated and will not be able to access the system.' : 'reactivated and will regain system access.'}
                                </p>
                                <div className="flex gap-3 mt-6">
                                    <Button variant="secondary" onClick={() => setConfirmAction(null)} className="flex-1">Cancel</Button>
                                    <Button
                                        variant={confirmAction === 'deactivate' ? 'danger' : 'success'}
                                        onClick={() => toggleMutation.mutate(confirmAction === 'activate')}
                                        isLoading={toggleMutation.isPending}
                                        className="flex-1">
                                        Confirm
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

// ============================================================
// MODAL COMPONENT
// ============================================================

const Modal = ({ open, onClose, title, icon, children }: { open: boolean; onClose: () => void; title: string; icon?: React.ReactNode; children: React.ReactNode }) => (
    <AnimatePresence>
        {open && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                onClick={onClose}>
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={e => e.stopPropagation()}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between p-6 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                            {icon && <div className="p-2 rounded-xl bg-slate-100">{icon}</div>}
                            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                            <X size={18} className="text-slate-400" />
                        </button>
                    </div>
                    <div className="p-6">{children}</div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
);